import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Trash2,
  Upload,
  Plus,
  X,
  Image,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { products, categories, conditions, bodyParts, rehabStages } from '../../data/mock';

const AdminProductEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';
  
  const existingProduct = !isNew ? products.find((p) => p.id === id) : null;

  const [formData, setFormData] = useState({
    name: existingProduct?.name || '',
    sku: existingProduct?.sku || '',
    shortDescription: existingProduct?.shortDescription || '',
    description: existingProduct?.description || '',
    price: existingProduct?.price || '',
    originalPrice: existingProduct?.originalPrice || '',
    cost: existingProduct?.cost || '',
    category: existingProduct?.category || '',
    stock: existingProduct?.stock || '',
    conditions: existingProduct?.conditions || [],
    bodyParts: existingProduct?.bodyParts || [],
    rehabStages: existingProduct?.rehabStage || [],
    whyItHelps: existingProduct?.whyItHelps || '',
    howToUse: existingProduct?.howToUse || [''],
    safetyWarnings: existingProduct?.safetyWarnings || [''],
    fastShipping: existingProduct?.fastShipping || false,
    bundleEligible: existingProduct?.bundleEligible || false,
    therapistRecommended: existingProduct?.badges?.includes('therapist-recommended') || false,
    requiresApproval: false,
  });

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    // Simulate save
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsSaving(false);
    navigate('/admin/products');
  };

  const addListItem = (field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], ''],
    }));
  };

  const updateListItem = (field, index, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].map((item, i) => (i === index ? value : item)),
    }));
  };

  const removeListItem = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const toggleArrayItem = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((v) => v !== value)
        : [...prev[field], value],
    }));
  };

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/products">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isNew ? 'Add New Product' : 'Edit Product'}
            </h1>
            <p className="text-gray-500">
              {isNew ? 'Create a new product in your catalog' : formData.name}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!isNew && (
            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Product'}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <Card className="border-gray-100">
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Product Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Premium Resistance Band Set"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="sku">SKU</Label>
                  <Input
                    id="sku"
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    placeholder="RB-PREM-001"
                    className="mt-1 font-mono"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="shortDescription">Short Description</Label>
                <Input
                  id="shortDescription"
                  value={formData.shortDescription}
                  onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
                  placeholder="Brief benefit statement"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="description">Full Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Detailed product description..."
                  className="mt-1 min-h-[120px]"
                />
              </div>
            </CardContent>
          </Card>

          {/* Media */}
          <Card className="border-gray-100">
            <CardHeader>
              <CardTitle>Media</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-red-300 hover:bg-red-50/50 transition-colors cursor-pointer">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Upload className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-gray-600 mb-1">Drag and drop images here, or click to browse</p>
                <p className="text-sm text-gray-400">PNG, JPG up to 10MB</p>
              </div>
              {existingProduct?.images && (
                <div className="flex gap-3 mt-4">
                  {existingProduct.images.map((img, index) => (
                    <div key={index} className="relative w-20 h-20 rounded-lg overflow-hidden group">
                      <img src={img} alt="" className="w-full h-full object-cover" />
                      <button className="absolute top-1 right-1 w-5 h-5 bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Clinical Content */}
          <Card className="border-gray-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Clinical Guidance Content
                <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  Required
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="whyItHelps">Why It Helps</Label>
                <Textarea
                  id="whyItHelps"
                  value={formData.whyItHelps}
                  onChange={(e) => setFormData({ ...formData, whyItHelps: e.target.value })}
                  placeholder="Explain how this product helps with rehabilitation..."
                  className="mt-1 min-h-[100px]"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>How to Use (Steps)</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => addListItem('howToUse')}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Step
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.howToUse.map((step, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                        {index + 1}
                      </span>
                      <Input
                        value={step}
                        onChange={(e) => updateListItem('howToUse', index, e.target.value)}
                        placeholder={`Step ${index + 1}`}
                        className="flex-1"
                      />
                      {formData.howToUse.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeListItem('howToUse', index)}
                          className="text-gray-400 hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    Safety Warnings
                  </Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => addListItem('safetyWarnings')}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Warning
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.safetyWarnings.map((warning, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        value={warning}
                        onChange={(e) => updateListItem('safetyWarnings', index, e.target.value)}
                        placeholder="Safety warning or contraindication"
                        className="flex-1"
                      />
                      {formData.safetyWarnings.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeListItem('safetyWarnings', index)}
                          className="text-gray-400 hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Pricing */}
          <Card className="border-gray-100">
            <CardHeader>
              <CardTitle>Pricing & Inventory</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="price">Price ($)</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="originalPrice">Compare at Price ($)</Label>
                <Input
                  id="originalPrice"
                  type="number"
                  step="0.01"
                  value={formData.originalPrice}
                  onChange={(e) => setFormData({ ...formData, originalPrice: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="cost">Cost ($)</Label>
                <Input
                  id="cost"
                  type="number"
                  step="0.01"
                  value={formData.cost}
                  onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="stock">Stock Quantity</Label>
                <Input
                  id="stock"
                  type="number"
                  value={formData.stock}
                  onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>

          {/* Category */}
          <Card className="border-gray-100">
            <CardHeader>
              <CardTitle>Organization</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="mb-2 block">Conditions</Label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {conditions.map((condition) => (
                    <label key={condition.id} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.conditions.includes(condition.id)}
                        onCheckedChange={() => toggleArrayItem('conditions', condition.id)}
                      />
                      <span className="text-sm">{condition.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Body Parts</Label>
                <div className="flex flex-wrap gap-2">
                  {bodyParts.map((part) => (
                    <button
                      key={part.id}
                      type="button"
                      onClick={() => toggleArrayItem('bodyParts', part.id)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        formData.bodyParts.includes(part.id)
                          ? 'bg-red-50 border-red-200 text-red-700'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {part.name}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Rehab Stages</Label>
                <div className="flex flex-wrap gap-2">
                  {rehabStages.map((stage) => (
                    <button
                      key={stage.id}
                      type="button"
                      onClick={() => toggleArrayItem('rehabStages', stage.id)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        formData.rehabStages.includes(stage.id)
                          ? 'bg-red-50 border-red-200 text-red-700'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {stage.name}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Options */}
          <Card className="border-gray-100">
            <CardHeader>
              <CardTitle>Options</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Fast Shipping</p>
                  <p className="text-xs text-gray-500">2-3 day delivery</p>
                </div>
                <Switch
                  checked={formData.fastShipping}
                  onCheckedChange={(checked) => setFormData({ ...formData, fastShipping: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Bundle Eligible</p>
                  <p className="text-xs text-gray-500">Can be added to kits</p>
                </div>
                <Switch
                  checked={formData.bundleEligible}
                  onCheckedChange={(checked) => setFormData({ ...formData, bundleEligible: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Therapist Recommended</p>
                  <p className="text-xs text-gray-500">Show recommendation badge</p>
                </div>
                <Switch
                  checked={formData.therapistRecommended}
                  onCheckedChange={(checked) => setFormData({ ...formData, therapistRecommended: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Requires Approval</p>
                  <p className="text-xs text-gray-500">Therapist approval needed</p>
                </div>
                <Switch
                  checked={formData.requiresApproval}
                  onCheckedChange={(checked) => setFormData({ ...formData, requiresApproval: checked })}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminProductEditPage;